import { FC, useEffect, useMemo } from 'react';
import { Box, VStack } from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from 'lib/redux';
import { Formik, Form } from 'formik';
import { ButtonSubmit, FormInput } from 'components';
import { getBio, selectBio, updateBio } from './creatorCardSlice';
import { cleanUrl, getDifferencesBetweenObjects } from 'lib/utils';
import { useToastNotification } from 'lib/hooks';
import { validateBio, validateEmail, validateName, validatePhone, validateUrl } from 'lib/utils/inputValidation';
import { object } from 'yup';
import { Field } from './types';

const creatorCardSchemaValidation = object().shape({
  [Field.name]: validateName.nullable(),
  [Field.email]: validateEmail.nullable(),
  [Field.description]: validateBio.nullable(),
  [Field.phone]: validatePhone.nullable(),
  [Field.website]: validateUrl.nullable(),
  [Field.linkedin]: validateUrl.nullable(),
  [Field.facebook]: validateUrl.nullable(),
  [Field.twitter]: validateUrl.nullable(),
  [Field.instagram]: validateUrl.nullable(),
});

export const CreatorCardForm: FC = () => {
  const dispatch = useAppDispatch();
  const bio = useAppSelector(selectBio);
  const { successToast, errorToast } = useToastNotification();

  const cleanBio = useMemo(() => {
    const cleanBio = {};

    for (const key in bio) {
      cleanBio[key] = bio[key] || '';
    }

    return cleanBio;
  }, [bio]);

  useEffect(() => {
    (async() => await dispatch(getBio()))();
  }, []);
  
  const onSubmit = async (values) => {
    try {
      let updatedFields = getDifferencesBetweenObjects(bio, values);
      const urlFields = [Field.website, Field.instagram, Field.linkedin, Field.facebook, Field.twitter];

      Object.keys(updatedFields).map((key: keyof typeof Field) => {
        if (!updatedFields[key]) {
          return;
        }

        if (!urlFields.includes(key as any)) {
          return;
        }

        updatedFields[key] = cleanUrl(updatedFields[key]);
      });

      await dispatch(updateBio(updatedFields)).unwrap();
      successToast({ title: 'Success', description: 'Your bio has been updated' });
    } catch (err) {
      errorToast({ title: 'Error', description: err });
    }
  };

  return (
    <Formik
      enableReinitialize
      onSubmit={onSubmit}
      initialValues={cleanBio}
      validationSchema={creatorCardSchemaValidation}
    >
      <Form>
        <VStack spacing={6}>
          <FormInput name={Field.name} label="Name" type="text" />
          <FormInput name={Field.email} label="Email" type="email" />
          <FormInput name={Field.description} label="Short Bio (up to 75 characters)" type="text" maxLength={75} />
          <FormInput name={Field.phone} label="Phone Number" type="text" />
          <FormInput name={Field.website} label="Personal Website" type="email" />
          <FormInput name={Field.instagram} label="Instagram" type="text" />
          <FormInput name={Field.facebook} label="Facebook" type="text" />
          <FormInput name={Field.linkedin} label="LinkedIn" type="text" />
          <FormInput name={Field.twitter} label="Twitter" type="text" />
        </VStack>
        <Box className="form-profile__footer">
          <ButtonSubmit>Update Bio</ButtonSubmit>
        </Box>
      </Form>
    </Formik>
  )
};
